<template>
  <section class="page">
    
    <div class="page__tabs">
      <button type="button" class="tabs__btn" :class="{ 'is-active': currentTab === 'list' }" @click="currentTab = 'list'">{{ $t('kps.personnel.tabList') }}</button>
      <button type="button" class="tabs__btn" :class="{ 'is-active': currentTab === 'archive' }" @click="currentTab = 'archive'">{{ $t('kps.personnel.tabArchive') }}</button>
    </div>

    <div class="page__head">
      <h2 class="page__title">{{ pageTitle }}</h2>
      <div class="page__search">
        <input class="page__search-input" v-model="q" type="text" :placeholder="$t('kps.personnel.searchPh')" :aria-label="$t('kps.personnel.searchPh')" />
        <span class="page__search-icon" aria-hidden="true"></span>
      </div>
    </div>

    
    <div class="page__table" v-if="currentTab === 'list'">
      <DataTable :columns="visibleColumns" :rows="filteredRows" :row-key="(r) => `book_${r.id}_${r.user_id}`" :row-height="tableRowHeight">
        <template #cell-num="{ row }">
          <div class="cell-text cell-text--center">{{ row.num }}</div>
        </template>
        <!-- По ТЗ 14.09.2026: реестр read-only. Все правки только через «Дело»
             (PersonnelDetailModal), чтобы данные не расходились между реестром
             и делом. Раньше inline-редактирование ФИО/звания/должности в
             реестре не сохраняло реальные поля User и создавало расхождения. -->
        <template #cell-rank="{ row }"><div class="cell-text">{{ row.rank_label || row.rank }}</div></template>
        <template #cell-position="{ row }"><div class="cell-text">{{ row.position_label || row.position }}</div></template>
        <template #cell-fio="{ row }"><div class="cell-text">{{ row.fio }}</div></template>
        <template #cell-birth="{ row }"><div class="cell-text">{{ row.birth }}</div></template>
        <template #cell-call="{ row }"><div class="cell-text">{{ row.call }}</div></template>
        <template #cell-arrive="{ row }"><div class="cell-text">{{ row.arrive }}</div></template>
        <template #cell-depart="{ row }"><div class="cell-text">{{ row.depart }}</div></template>
        <template #cell-note="{ row }"><div class="cell-text">{{ row.note }}</div></template>
        <template #cell-order="{ row }"><div class="cell-text">{{ row.order }}</div></template>
        <template #cell-reinforcement="{ row }">
          <label class="cell-reinforcement" :title="$t('kps.personnel.reinforcementHint')">
            <input
              type="checkbox"
              :checked="row.is_reinforcement"
              :disabled="readOnly || row.__new"
              @change="onToggleReinforcement(row, $event.target.checked)"
            />
          </label>
        </template>

        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <!-- Дело — единственная точка редактирования данных военнослужащего. -->
            <button v-if="!row.__new" class="actions__btn actions__btn--card" @click="openDetail(row)" :title="$t('kps.personnel.caseTitle')" :aria-label="$t('kps.personnel.case')">
              <span class="actions__label">{{ $t('kps.personnel.case') }}</span>
            </button>
            <button class="actions__btn actions__btn--archive" @click="onArchive(row)" :title="$t('kps.personnel.archiveRow')" :aria-label="$t('kps.personnel.archiveRow')">
              <img class="actions__icon" src="../../assets/images/archive.svg" alt="archive" />
            </button>
          </div>
        </template>
      </DataTable>
    </div>


    <div class="page__table" v-else>
      <DataTable :columns="visibleColumns" :rows="filteredArchivedRows" :row-key="(r) => r.id" :row-height="tableRowHeight">
        <template #cell-num="{ row }"><div class="cell-text cell-text--center">{{ row.num }}</div></template>
        <template #cell-rank="{ row }"><div class="cell-text">{{ row.rank_label || row.rank }}</div></template>
        <template #cell-position="{ row }"><div class="cell-text">{{ row.position_label || row.position }}</div></template>
        <template #cell-fio="{ row }"><div class="cell-text">{{ row.fio }}</div></template>
        <template #cell-birth="{ row }"><div class="cell-text">{{ row.birth }}</div></template>
        <template #cell-call="{ row }"><div class="cell-text">{{ row.call }}</div></template>
        <template #cell-arrive="{ row }"><div class="cell-text">{{ row.arrive }}</div></template>
        <template #cell-depart="{ row }"><div class="cell-text">{{ row.depart }}</div></template>
        <template #cell-note="{ row }"><div class="cell-text">{{ row.note }}</div></template>
        <template #cell-order="{ row }"><div class="cell-text">{{ row.order }}</div></template>
        <template #cell-reinforcement="{ row }">
          <div class="cell-text cell-text--center">{{ row.is_reinforcement ? '✓' : '' }}</div>
        </template>
        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button class="actions__btn actions__btn--edit" @click="onRestore(row)" :title="$t('kps.personnel.restoreFromArchive')" :aria-label="$t('kps.personnel.restore')">
              <img class="actions__icon" src="../../assets/images/unarchive.svg" alt="restore" />
            </button>
          </div>
        </template>
      </DataTable>
    </div>

    <div class="page__footer">
      <ActionButton v-if="!readOnly && currentTab === 'list'" :color="'#2A8A1B'" :text="$t('kps.personnel.addRow')" @click="addRow" />
      <ViewNavButtons class="page__footer-nav" :prev-text="$t('common.back')" :next-text="$t('common.next')" :prev-to="{ path: '/kps/map' }" :next-to="{ path: '/kps/orders' }" />
    </div>

    <PersonnelDetailModal
      :open="detail.open"
      :person-id="detail.personId"
      @close="detail.open = false"
      @saved="onDetailSaved"
    />
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue';
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue';
import ActionButton from '@/components/widgets/ActionButton.vue';
import MaskedInput from '@/components/widgets/MaskedInput.vue';
import SelectField from '@/components/widgets/SelectField.vue';
import DatePicker from '@/components/widgets/DatePicker.vue';
import PersonnelDetailModal from '@/components/modals/PersonnelDetailModal.vue';
import { listBookUsers, restoreBookUser, archiveUser as archiveBookUser, updateBookUser, setReinforcement } from '@/api/bookUsers';
import { ensureTodayBookContext, refreshTodayBookContext } from '@/api/book';
import { getPositions, getRanks, createUser, updateUser, linkUserToBook, deleteUser } from '@/api/personnel';
import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user';
import { canEdit } from '@/access/armAccess';

export default {
  name: 'KpsPersonnelView',
  components: { DataTable, ViewNavButtons, ActionButton, MaskedInput, SelectField, DatePicker, PersonnelDetailModal },
  data() {
    return {
      currentUser: null,
      currentTab: 'list',
      q: '',
      tableRowHeight: 114,
      // По ТЗ 03.09.2026: колонки соразмерны, ФИО растяжимая; сумма фикс
      // помещается в контейнер, чтобы не было горизонтального скролла.
      // columns: computed below
      rows: [],
      archived: [],
      rankOptions: [],
      positionOptions: [],
      loading: false,
      error: null,
      // Модалка «Личное дело» — 5 вкладок, ~110 полей по Figma «Проектирование»
      detail: { open: false, personId: null },
    };
  },
  computed: {
    /**
     * По ТЗ (встреча 27.08.2026): раздел 2 (Список ЛС) заполняет и редактирует
     * АДМИНИСТРАТОР. Начальник заставы может только читать и дополнять строки
     * усиления (номера 41+). Прочие роли — read-only.
     */
    canEditPersonnel() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/personnel')
    },
    readOnly() { return !this.canEditPersonnel },
    columns() {
      return [
        { key: 'num',      label: this.$t('kps.personnel.colStaffNum'),   width: 120 },
        { key: 'rank',     label: this.$t('kps.personnel.colRank'),       width: 110 },
        { key: 'position', label: this.$t('kps.personnel.colPosition'),   width: 110 },
        { key: 'fio',      label: this.$t('kps.personnel.colFio'),        width: 190, flex: true },
        { key: 'birth',    label: this.$t('kps.personnel.colBirth'),      width: 110 },
        { key: 'call',     label: this.$t('kps.personnel.colCall'),       width: 130 },
        { key: 'arrive',   label: this.$t('kps.personnel.colArrive'),     width: 130 },
        { key: 'depart',   label: this.$t('kps.personnel.colDepart'),     width: 130 },
        { key: 'note',     label: this.$t('kps.personnel.colNote'),       width: 110 },
        { key: 'order',    label: this.$t('kps.personnel.colOrderShort'), width: 150 },
        { key: 'reinforcement', label: this.$t('kps.personnel.colReinforcement'), width: 120 },
        { key: 'actions',  label: '',                                     width: 150 },
      ]
    },
    visibleColumns() {
      return this.readOnly ? this.columns.filter(c => c.key !== 'actions') : this.columns;
    },
    pageTitle() {
      return this.$route.meta?.headerTitle || this.$t('kps.personnel.title')
    },
    filteredRows() {
      const qq = this.q.trim().toLowerCase();
      if (!qq) return this.rows;
      return this.rows.filter(r => [r.num, r.rank, r.position, r.fio, r.birth, r.call, r.arrive, r.depart, r.note, r.order].join(' ').toLowerCase().includes(qq));
    },
    filteredArchivedRows() {
      const qq = this.q.trim().toLowerCase();
      if (!qq) return this.archived;
      return this.archived.filter(r => [r.num, r.rank, r.position, r.fio, r.birth, r.call, r.arrive, r.depart, r.note, r.order].join(' ').toLowerCase().includes(qq));
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.initLoad();
  },
  methods: {
    async initLoad() {
      this.loading = true; this.error = null;
      try {
        const ctx = await ensureTodayBookContext();
        this.bookId = ctx?.id;
        // Важно: сначала загружаем справочники, потом строки
        await this.fetchDictionaries();
        await this.fetchRows();
      } catch (e) {
        this.error = e?.message || this.$t('kps.personnel.loadError');
      } finally { this.loading = false; }
    },
    async fetchDictionaries() {
      try {
        const [ranks, positions] = await Promise.all([getRanks(), getPositions()]);
        this.rankOptions = ranks.map(r => ({ value: r.id, label: r.name || r.title }));
        this.positionOptions = positions.map(p => ({ value: p.id, label: p.name || p.title }));
      } catch (e) {
        console.error('Ошибка загрузки справочников', e);
      }
    },
    async fetchRows() {
      const activeListRaw = await listBookUsers({ is_archived: false });
      const archivedListRaw = await listBookUsers({ is_archived: true });
      // По ТЗ 02.09.2026: /api/book/users/ отдавал дубли по всем книгам —
      // дедуплицируем по user.id (пока не отфильтрует бэк по текущей книге).
      const dedup = (list) => {
        const seen = new Set()
        return (Array.isArray(list) ? list : []).filter(bu => {
          const uid = (bu.user && bu.user.id) || bu.user_id
          if (!uid || seen.has(uid)) return false
          seen.add(uid); return true
        })
      }
      const activeList = dedup(activeListRaw)
      const archivedList = dedup(archivedListRaw)
      
      const getRankLabel = (id) => this.rankOptions.find(o => o.value === id)?.label || '';
      const getPosLabel = (id) => this.positionOptions.find(o => o.value === id)?.label || '';

      const mapUser = (u) => {
        // Если rank/position приходят объектами или ID
        let rankId = (typeof u.rank === 'object' && u.rank) ? u.rank.id : (u.rank_id || u.rank);
        let posId = (typeof u.position === 'object' && u.position) ? u.position.id : (u.position_id || u.position);
        
        // Если rank/position - это строки (названия), пытаемся найти ID в справочниках
        if (typeof u.rank === 'string' && isNaN(u.rank)) {
          const found = this.rankOptions.find(o => o.label === u.rank);
          rankId = found ? found.value : '';
        }
        if (typeof u.position === 'string' && isNaN(u.position)) {
          const found = this.positionOptions.find(o => o.label === u.position);
          posId = found ? found.value : '';
        }
        
        // Пытаемся найти лейбл в справочнике, если нет - берем из объекта или поля label
        const rankLabel = getRankLabel(rankId) || ((typeof u.rank === 'object') ? u.rank.name : (typeof u.rank === 'string' ? u.rank : '')) || '';
        const posLabel = getPosLabel(posId) || ((typeof u.position === 'object') ? u.position.name : (typeof u.position === 'string' ? u.position : '')) || '';

        return {
          id: u.id, // ID связи (book_user_id)
          user_id: u.user_id || u.id, // ID пользователя
          personal_number: u.personal_number ?? '',
          rank: rankId || '',
          rank_label: rankLabel,
          position: posId || '',
          position_label: posLabel,
          fio: u.fio || u.full_name || '',
          birth: this.formatDate(u.birth_date),
          call: this.formatMonthYear(u.service_start_date || u.call_date),
          arrive: u.arrive_from || u.arrival_info || '',
          depart: u.depart_to || u.departure_info || '',
          note: u.note || '',
          order: u.order_ref || u.appointment_info || '',
          is_reinforcement: !!u.is_reinforcement,
          editing: false,
        };
      };

      this.rows = activeList.map(mapUser).map((r, idx) => ({
        ...r,
        num: r.personal_number !== '' && r.personal_number != null ? String(r.personal_number) : String(idx + 1),
      }));
      this.archived = archivedList.map(mapUser).map((r, idx) => ({
        ...r,
        num: r.personal_number !== '' && r.personal_number != null ? String(r.personal_number) : String(idx + 1),
      }));
    },
    onEdit(row) {
      row.__backup = { ...row };
      row.editing = true;
    },
    async onAccept(row) {
      const payload = {
        rank: row.rank,
        position: row.position,
        full_name: row.fio,
        birth_date: this.parseDate(row.birth),
        service_start_date: this.parseMonthYear(row.call),
        appointment_info: row.order,
        arrival_info: row.arrive || null,
        departure_info: row.depart || null,
      };

      const errs = [];
      if (!payload.full_name || !String(payload.full_name).trim()) errs.push(this.$t('kps.personnel.reqFio'));
      if (!payload.rank) errs.push(this.$t('kps.personnel.reqRank'));
      if (!payload.birth_date) errs.push(this.$t('kps.personnel.reqBirth'));
      if (!payload.service_start_date) errs.push(this.$t('kps.personnel.reqCall'));
      if (errs.length) {
        this.error = this.$t('kps.personnel.requiredMissing', { fields: errs.join(', ') });
        return;
      }

      try {
        if (row.__new) {
          const newUser = await createUser(payload);

          // Link to current book and keep both IDs correctly.
          if (this.bookId) {
            const link = await linkUserToBook({ book_id: this.bookId, user_id: newUser.id });
            row.id = link?.id || row.id; // row.id is book_user_id in this table
            row.book_user_id = link?.id || row.book_user_id;
          }

          row.user_id = newUser.id;
          row.__new = false;

          // Save note into book user entry if provided
          if (row.note && (row.book_user_id || row.id)) {
            await updateBookUser(row.book_user_id || row.id, { description: row.note });
          }
        } else {
          const uid = row.user_id || row.id;
          await updateUser(uid, payload);
          // Сохраняем note отдельно в book/users/{id}/ как description
          const bookUserId = row.book_user_id || row.id;
          if (bookUserId) await updateBookUser(bookUserId, { description: row.note || null });
        }
        row.editing = false;
        delete row.__backup;
        // Обновляем список чтобы подтянулись правильные лейблы званий/должностей
        refreshTodayBookContext();
        await this.fetchRows(); 
      } catch (e) {
        if (row.__backup) { Object.assign(row, row.__backup); delete row.__backup; }
        // row.editing = false; // Не сбрасываем, чтобы можно было исправить
        console.error('Ошибка сохранения (book)', e);
        const detail = e?.payload?.detail || e?.payload?.message;
        const full = (!detail && e?.payload) ? JSON.stringify(e.payload) : '';
        this.error = this.$t('kps.personnel.saveError') + ': ' + (detail || full || e.message);
      }
    },
    onDecline(row) {
      if (row.__new) {
        this.rows = this.rows.filter(r => r !== row);
        return;
      }
      if (row.__backup) {
        const b = row.__backup;
        Object.keys(b).forEach(k => { if (k !== '__backup') row[k] = b[k]; });
        delete row.__backup;
      }
      row.editing = false;
    },
    async onArchive(row) {
      try {
        // Используем book_user_id (row.id) для архивации связи
        await archiveBookUser(row.id);
        refreshTodayBookContext();
        await this.fetchRows();
      } catch (e) {
        console.error('Ошибка архивации (book)', e);
        this.error = this.$t('kps.personnel.archiveError');
      }
    },
    async onToggleReinforcement(row, checked) {
      const prev = !!row.is_reinforcement
      row.is_reinforcement = !!checked
      try {
        await setReinforcement(row.id, !!checked)
      } catch (e) {
        row.is_reinforcement = prev
        console.error('Ошибка обновления флага усиления', e)
        this.error = this.$t('kps.personnel.reinforcementError')
      }
    },
    async onRestore(row) {
      try {
        const idKey = row.id;
        await restoreBookUser(idKey);
        await this.fetchRows();
      } catch (e) {
        console.error('Ошибка восстановления (book)', e);
        this.error = this.$t('kps.personnel.restoreError');
      }
    },
    openDetail(row) {
      // Модалка «Личное дело» тянет /users/admin/{pk}/ по user_id (НЕ book_user_id).
      // row.id — это book_user_id (id связки book↔user); фолбэк на него дал бы 404.
      const pid = row.user_id
      if (!pid) return
      this.detail.personId = pid
      this.detail.open = true
    },
    async onDetailSaved() {
      // По ТЗ 14.09.2026: реестр = проекция дела. После сохранения в модалке
      // сбрасываем кэш book-context (иначе fetchRows читает старые данные
      // из bookContext.users) и перечитываем строки.
      refreshTodayBookContext()
      try { await this.fetchRows() } catch (_) { /* silent */ }
    },
    addRow() {
      const nextNum = this.rows.length + 1;
      const tempId = Date.now();
      const newRow = {
        id: tempId,
        num: String(nextNum),
        rank: null, position: null, fio: '', birth: '', call: '', arrive: '', depart: '', note: '', order: '',
        editing: true,
        __new: true
      };
      this.rows = [...this.rows, newRow];
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-');
        return `${d}.${m}.${y}`;
      }
      return dateStr;
    },
    parseDate(dateStr) {
      if (!dateStr) return null;
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split('.');
        return `${y}-${m}-${d}`;
      }
      return null;
    },
    formatMonthYear(dateStr) {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-');
        return `${d}.${m}.${y}`;
      }
      return dateStr;
    },

    formatToDateInput(dateStr) {
      // Конвертирует ДД.ММ.ГГГГ -> ГГГГ-ММ-ДД
      if (!dateStr) return '';
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('.');
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    },
    formatFromDateInput(dateStr) {
      // Конвертирует ГГГГ-ММ-ДД -> ДД.ММ.ГГГГ
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
      }
      return dateStr;
    },

    parseMonthYear(dateStr) {
      if (!dateStr) return null;
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        const [d, m, y] = dateStr.split('.');
        return `${y}-${m}-${d}`;
      }
      return null;
    }
  }
};
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}


.page__tabs { display: inline-flex; gap: 8px; }
.tabs__btn { border: 1px solid #E5E7EB; background: #fff; color: #111827; border-radius: 8px; padding: 8px 12px; cursor: pointer; font-size: 14px; }
.tabs__btn.is-active { background: #2F78EE; color: #fff; border-color: #2F78EE; }

.page__title { margin: 0; color: #111827; font-size: 24px; font-weight: 700; line-height: 33.6px; }

.page__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.page__head .page__title { flex: 1 1 auto; min-width: 0; }

.page__search { width: 320px; max-width: 100%; padding: 8px 14px; border-radius: 10px; outline: 1px #E9EAEC solid; outline-offset: -1px; display: inline-flex; align-items: center; gap: 10px; background: #FFFFFF; box-sizing: border-box; flex: 0 0 auto; }
.page__search-input { flex: 1 1 0; border: none; outline: none; background: transparent; color: #111827; font-size: 14px; line-height: 22.4px; font-family: 'Times New Roman', Times, serif; }
.page__search-input::placeholder { color: #A0AEC0; }
.page__search-icon { width: 20px; height: 20px; background: no-repeat center/contain url('@/assets/images/search.svg'); display: inline-block; }

.page__table { width: 100%; }

.cell-text--center { width: 100%; text-align: center; }

.cell-text { flex: 1 1 0; color: #111827; font-size: 16px; font-family: 'Times New Roman', Times, serif; font-weight: 400; line-height: 22.2px; width: 100%; }
.tbl-input { width: 100%; height: 32px; padding: 6px 10px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 16px; line-height: 22.2px; font-family: 'Times New Roman', Times, serif; color: #111827; background: #fff; }
.tbl-input:focus { outline: 2px solid rgba(47,120,238,.25); outline-offset: 0; }

/* Prevent date value truncation in narrow cells (Chrome). */
.tbl-input[type="date"] {
  min-width: 150px;
}

.actions { display: inline-flex; align-items: center; gap: 10px; justify-content: flex-end; width: 100%; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-family: Arial, Helvetica, sans-serif; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--edit { background: #2F78EE; }
.actions__btn--accept { background: #2A8A1B; }
.actions__btn--decline { background: #D81717; }
.actions__btn--archive { background: #FFC600; }
.actions__btn--card { background: #6366F1; color: #fff; width: auto; padding: 6px 10px; font-size: 12px; font-weight: 600; }
.actions__label { color: #fff; font-family: Arial, Helvetica, sans-serif; line-height: 1; }

.page__pagination { display: flex; }
.page__footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page__footer-nav { margin-left: auto; }
</style>
